/**
 * Configuration Service
 *
 * Centralized configuration management using environment variables.
 * Provides type-safe access to application settings with sensible defaults.
 */

import { log } from '../utils/DebugLogger'

export interface AuthConfig {
  loginUrl: string
  logoutUrl: string
  redirectParam: string
  sessionTimeout: number
  authServiceUrl: string
  cookieName: string
  cognitoBackEndUrl: string
  tokenRefreshThreshold: number
  tokenRefreshInterval: number
}

export interface CognitoEndpoints {
  login: string
  register: string
  confirmAccount: string
  resendConfirmation: string
  forgotPassword: (username: string) => string
  resetPassword: string
  refreshToken: string
  logout: string
  globalLogout: string
  verifySession: string
  users: string
  createUser: string
  updateUser: string
  deleteUser: (username: string) => string
  changePassword: string
  toggleUserGroup: (username: string, group: string) => string
  userGroups: string
  getUserBySub: (sub: string) => string
  b2bUsers: string
  b2bCreateUser: string
}

export interface ApiEndpoints {
  permissions: string
  logout: string
  filters: string
  translations: string
  cognito: CognitoEndpoints
}

export interface ApiConfig {
  sistemaHost: string
  tenantConfigHost: string
  idiomasHost: string
  cognitoHost: string
  agentHost: string
  messagingServiceUrl: string
  baseUrl: string
  timeout: number
  endpoints: ApiEndpoints
}

export interface AppConfig {
  appVersion: string
  permissionsRefreshInterval: number
  permissionsAutoRefresh: boolean
  paginationDefaultPageSize: number
  paginationPageSizeOptions: number[]
  defaultLocale: string
  /** Tópico ntfy.sh para erros de runtime do frontend; vazio = relatório desligado. */
  ntfyErrorsTopic: string
}

export interface DevConfig {
  showPerformance: boolean
  mockData: boolean
}

const parseBoolean = (value: unknown, defaultValue: boolean = false): boolean => {
  if (value === undefined || value === null || value === '') {
    return defaultValue
  }
  return value === 'true' || value === '1' || value === true
}

const parseNumber = (value: unknown, defaultValue: number = 0): number => {
  if (value === undefined || value === null || value === '') {
    return defaultValue
  }
  const parsed = Number(value)
  return isNaN(parsed) ? defaultValue : parsed
}

const parseArray = (value: unknown, defaultValue: string[] = []): string[] => {
  if (!value || typeof value !== 'string') return defaultValue
  return value.split(',').map(item => item.trim())
}

class ConfigService {
  env: ImportMetaEnv
  mode: string
  baseUrl: string
  isDevelopment: boolean
  isProduction: boolean
  debugMode: boolean
  auth!: AuthConfig
  api!: ApiConfig
  app!: AppConfig
  dev!: DevConfig
  private disabledFeatures: Set<string>

  constructor() {
    this.env = import.meta.env
    this.mode = import.meta.env.MODE || 'production'
    // Router base path — Vite injects it from `base` in vite.config (default '/').
    this.baseUrl = import.meta.env.BASE_URL || '/'
    this.isDevelopment = this.mode === 'development'
    this.isProduction = this.mode === 'production'
    this.debugMode = parseBoolean(this.env.VITE_DEBUG_MODE, this.isDevelopment)
    // Seeded at bootstrap from GET /api/tenant_config/config → disabled_features
    // (the backend feature registry, config/features.php). Empty until then.
    this.disabledFeatures = new Set()

    this._initializeAuth()
    this._initializeAPI()
    this._initializeApp()
    this._initializeDevelopment()

    this._validate()

    if (this.debugMode) {
      this._logConfiguration()
    }
  }

  private _initializeAuth(): void {
    this.auth = {
      loginUrl: this.env.VITE_AUTH_LOGIN_URL || (this.isDevelopment ? 'http://localhost/login/' : '/login/'),
      logoutUrl: this.env.VITE_AUTH_LOGOUT_URL || (this.isDevelopment ? 'http://localhost/index.php/usuario/logout' : '/index.php/usuario/logout'),
      redirectParam: this.env.VITE_AUTH_REDIRECT_PARAM || 'redirectTo',
      sessionTimeout: parseNumber(this.env.VITE_AUTH_SESSION_TIMEOUT, 3600),
      authServiceUrl: this.env.VITE_AUTH_SERVICE_URL || (this.isDevelopment ? 'http://localhost:35600' : 'https://auth.franchisefactory.com.br'),
      cookieName: this.env.VITE_COOKIE_NAME || 'FranchiseFactory',
      cognitoBackEndUrl: '/loginApi',
      tokenRefreshThreshold: parseNumber(this.env.VITE_TOKEN_REFRESH_THRESHOLD, 80),
      tokenRefreshInterval: parseNumber(this.env.VITE_TOKEN_REFRESH_INTERVAL, 60),
    }
  }

  private _initializeAPI(): void {
    const sistemaHost = this.env.VITE_SISTEMA_HOST || 'http://localhost:8080'
    const tenantConfigHost = this.env.VITE_TENANT_CONFIG_HOST || sistemaHost
    const idiomasHost = this.env.VITE_IDIOMAS_HOST || sistemaHost
    const cognitoHost = this.env.VITE_COGNITO_HOST || sistemaHost
    const agentHost = this.env.VITE_AGENT_HOST || 'http://localhost:35541'
    // Unified Messaging Service (Go). VITE_INSTAGRAM_MESSAGING_HOST is the legacy
    // name kept as a fallback; '' means "not configured" (consumers warn).
    const messagingServiceUrl = this.env.VITE_MESSAGING_SERVICE_HOST
      || this.env.VITE_INSTAGRAM_MESSAGING_HOST
      || ''

    this.api = {
      sistemaHost,
      tenantConfigHost,
      idiomasHost,
      cognitoHost,
      agentHost,
      messagingServiceUrl,
      baseUrl: sistemaHost,
      timeout: parseNumber(this.env.VITE_API_TIMEOUT, 30000),
      endpoints: {
        permissions: sistemaHost + '/permissoes/pegaPermissoes',
        logout: sistemaHost + '/usuario/logout',
        filters: sistemaHost + '/filtroApi',
        translations: idiomasHost + '/idiomas/pegaIdioma',
        cognito: {
          login: cognitoHost + '/v1/user/logaUsuario',
          register: cognitoHost + '/v1/user/registraUsuario',
          confirmAccount: cognitoHost + '/v1/user/confirmaUsuario',
          resendConfirmation: cognitoHost + '/v1/user/reenviarConfirmacao',
          forgotPassword: (username: string) => `${cognitoHost}/v1/user/esqueciMinhaSenha/${username}`,
          resetPassword: cognitoHost + '/v1/user/confirmaEsqueciMinhaSenha',
          refreshToken: cognitoHost + '/v1/user/refreshToken',
          logout: cognitoHost + '/v1/user/logout',
          globalLogout: cognitoHost + '/v1/user/globalLogout',
          verifySession: cognitoHost + '/v1/user/verificaSeEstaLogado',
          users: cognitoHost + '/v1/admin/usuarios',
          createUser: cognitoHost + '/v1/admin/createUser',
          updateUser: cognitoHost + '/v1/admin/updateUser',
          deleteUser: (username: string) => `${cognitoHost}/v1/admin/deletaUsuario/${username}`,
          changePassword: cognitoHost + '/v1/admin/changePassword',
          toggleUserGroup: (username: string, group: string) => `${cognitoHost}/v1/admin/toggleGrupoUsuario/${username}/${group}`,
          userGroups: cognitoHost + '/v1/admin/userGroups',
          getUserBySub: (sub: string) => `${cognitoHost}/v1/b2b/usuarios/${sub}`,
          b2bUsers: cognitoHost + '/v1/b2b/usuarios',
          b2bCreateUser: cognitoHost + '/v1/b2b/usuarios'
        }
      }
    }
  }

  private _initializeApp(): void {
    this.app = {
      // Build version tag — injected by CI as VITE_APP_VERSION (git tag) at Vite build time
      appVersion: this.env.VITE_APP_VERSION || 'dev',
      permissionsRefreshInterval: parseNumber(this.env.VITE_PERMISSIONS_REFRESH_INTERVAL, 10),
      permissionsAutoRefresh: parseBoolean(this.env.VITE_PERMISSIONS_AUTO_REFRESH, true),
      paginationDefaultPageSize: parseNumber(this.env.VITE_PAGINATION_DEFAULT_PAGE_SIZE, 20),
      paginationPageSizeOptions: parseArray(
        this.env.VITE_PAGINATION_PAGE_SIZE_OPTIONS,
        ['10', '20', '50', '100']
      ).map(Number),
      defaultLocale: this.env.VITE_DEFAULT_LOCALE || 'pt-BR',
      // Tópico ntfy.sh para erros de runtime do frontend (vazio = desligado).
      // Definido só nas variantes de build doutor/ohmybox (.env.sistema-front.*).
      ntfyErrorsTopic: this.env.VITE_NTFY_ERRORS_TOPIC || '',
    }
  }

  private _initializeDevelopment(): void {
    this.dev = {
      showPerformance: parseBoolean(this.env.VITE_SHOW_PERFORMANCE, this.isDevelopment),
      mockData: parseBoolean(this.env.VITE_MOCK_DATA, false),
    }
  }

  private _validate(): void {
    const errors: string[] = []

    if (!this.auth.loginUrl) errors.push('VITE_AUTH_LOGIN_URL is required')
    if (!this.auth.logoutUrl) errors.push('VITE_AUTH_LOGOUT_URL is required')
    if (this.app.permissionsRefreshInterval <= 0) errors.push('VITE_PERMISSIONS_REFRESH_INTERVAL must be greater than 0')
    if (this.api.timeout <= 0) errors.push('VITE_API_TIMEOUT must be greater than 0')

    // In production VITE_AGENT_HOST MUST be set to the deployed agent host. If it is
    // blank, agentHost silently falls back to localhost:35541 and the assistant POSTs
    // to the END USER's own machine. Warn loudly (don't throw — the assistant is gated
    // behind specific user groups and must not block the rest of the SPA from booting).
    if (this.isProduction && !this.env.VITE_AGENT_HOST) {
      console.error('[ConfigService] VITE_AGENT_HOST is not set in production — the AI assistant will fail (falling back to localhost:35541). Set it in the deploy env file.')
    }

    if (errors.length > 0) {
      console.error('[ConfigService] Configuration validation failed:')
      errors.forEach(error => console.error(`  - ${error}`))
      throw new Error('Invalid configuration. Check console for details.')
    }
  }

  isFeatureEnabled(featureName: string): boolean {
    return !this.disabledFeatures.has(featureName.trim().toLowerCase())
  }

  /**
   * Replace the disabled-feature set with the tenant's resolved list. Called
   * once by bootstrap/tenant.ts before the router or any gated component runs.
   */
  setDisabledFeatures(items: string[]): void {
    this.disabledFeatures = new Set(
      items.map(s => s.trim().toLowerCase()).filter(s => s.length > 0)
    )
    log('ConfigService', 'setDisabledFeatures', 'Disabled features for this tenant', {
      disabledFeatures: [...this.disabledFeatures]
    })
  }

  getApiUrl(path?: string): string {
    if (!path) return this.api.baseUrl

    if (path.startsWith('http://') || path.startsWith('https://')) {
      return path
    }

    const base = this.api.baseUrl || ''
    const cleanPath = path.startsWith('/') ? path : `/${path}`

    return `${base}${cleanPath}`
  }

  getLoginUrl(redirectPath?: string): string {
    if (!redirectPath) return this.auth.loginUrl

    const separator = this.auth.loginUrl.includes('?') ? '&' : '?'
    return `${this.auth.loginUrl}${separator}${this.auth.redirectParam}=${encodeURIComponent(redirectPath)}`
  }

  private _logConfiguration(): void {
    log('ConfigService', 'logConfiguration', 'Configuration loaded:', {
      mode: this.mode,
      debugMode: this.debugMode,
      disabledFeatures: [...this.disabledFeatures],
      auth: this.auth,
      api: this.api,
      app: this.app,
      dev: this.dev
    })
  }

  getEnv<T = string>(key: string, defaultValue: T | null = null): T | null {
    const value = (this.env as Record<string, unknown>)[key]
    return value !== undefined ? (value as T) : defaultValue
  }
}

export const config = new ConfigService()
export { ConfigService }
export default config
